import { useState, type ReactNode } from "react";
import { Button, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { api, errorMessage } from "../helpers/api";
import { exportTableToCsv, type ExportColumns } from "../helpers/csv";
import { useUser } from "../context/UserContext";
import type { Paginated } from "../api-models";

// The server caps pageSize (api.go), so a whole-list export is walked a page at a time.
const EXPORT_PAGE_SIZE = 200;
// Enough for any register in this system; past it the user wants a filter, not a bigger file.
const MAX_EXPORT_ROWS = 10000;

interface TableDownloadProps<T> {
  /** Report name — the first header line of the file, and the base of its file name. */
  name: string;
  /** The table's own columns: the export is the table, same columns, same order. */
  columns: ExportColumns<T>;
  /** Rows already loaded. Omit when `endpoint` should be re-read for every matching row. */
  dataSource?: readonly T[];
  /** A paginated list endpoint — the export walks every page under `params`, not just the one on screen. */
  endpoint?: string;
  params?: Record<string, unknown>;
  /** Recorded on the Filters header line, so the file says what it is a view of. */
  filters?: Record<string, unknown>;
  /** Extra `label,value` header lines (a report's caveat, a period, a total). */
  extraHeaders?: [string, ReactNode][];
  fileName?: string;
  disabled?: boolean;
  label?: string;
  /** Returns a message to block the download with (nothing selected yet, and the like). */
  beforeDownload?: () => string | null;
}

/**
 * The one CSV export button. Every table that can be exported goes through it, so every
 * file looks the same: a header block naming the report, the person, the time and the
 * filters, then the table exactly as rendered on screen (architecture.md §12).
 */
export function TableDownload<T extends object>({
  name,
  columns,
  dataSource,
  endpoint,
  params,
  filters,
  extraHeaders = [],
  fileName,
  disabled,
  label = "Export CSV",
  beforeDownload,
}: TableDownloadProps<T>) {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);

  async function fetchAllRows(): Promise<{ rows: T[]; truncated: boolean }> {
    const rows: T[] = [];
    for (let page = 1; ; page++) {
      const res = await api.get<Paginated<T> | T[]>(endpoint!, {
        params: { ...params, page, pageSize: EXPORT_PAGE_SIZE },
      });
      // Reports answer with a plain array; registers with a paginated envelope.
      if (Array.isArray(res.data)) return { rows: res.data, truncated: false };
      const { data = [], count = 0, meta } = res.data;
      rows.push(...data);
      // An uncounted register (the audit log) reports the next page itself; a counted one
      // is walked until its total is reached.
      const more = meta?.hasMore ?? !(rows.length >= count || data.length < EXPORT_PAGE_SIZE);
      if (!more) return { rows, truncated: false };
      if (rows.length >= MAX_EXPORT_ROWS) return { rows, truncated: true };
    }
  }

  async function handleClick() {
    const blocked = beforeDownload?.();
    if (blocked) {
      message.error(blocked);
      return;
    }
    setLoading(true);
    try {
      let rows = (dataSource ?? []) as readonly T[];
      let truncated = false;
      if (!dataSource && endpoint) ({ rows, truncated } = await fetchAllRows());
      if (rows.length === 0) {
        message.info("Nothing to export.");
        return;
      }
      exportTableToCsv({
        name,
        columns,
        rows,
        filters,
        fileName,
        generatedBy: user ? `${user.name}${user.email ? ` (${user.email})` : ""}` : undefined,
        extraHeaders: truncated
          ? [...extraHeaders, ["Note", `Export capped at ${MAX_EXPORT_ROWS.toLocaleString()} rows — narrow the filters for the rest.`]]
          : extraHeaders,
      });
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Button icon={<DownloadOutlined />} loading={loading} disabled={disabled} onClick={handleClick}>
      {label}
    </Button>
  );
}
